import React from "react";
import { Button, Form, Input, Typography } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

const { Text } = Typography;

/**
 * Recent lab needs. Each panel is one row: the panel name on the left and its
 * readings running across beside it, the next panel on the row below. Every
 * reading is a plain label with a small box next to it.
 *
 * The labels are written as ordinary text rather than Form.Item `label`
 * props: antd's vertical form stacks its own labels above the control and
 * restyles them, which fought this layout.
 *
 * Field paths write straight into the stored shape:
 *   investigations: { abg: {...}, cbp: {...}, crp, rft: {...}, ecg, others: [] }
 */
export const INVESTIGATION_GROUPS = [
  {
    key: "abg",
    label: "ABG",
    fields: [
      { name: "ph", label: "pH" },
      { name: "pco2", label: "pCO2" },
      { name: "po2", label: "pO2" },
      { name: "hco3", label: "HCO3+" },
      { name: "so2", label: "SO2" },
    ],
  },
  {
    key: "cbp",
    label: "CBP",
    fields: [
      { name: "hb", label: "Hb" },
      { name: "tlc", label: "TLC" },
      { name: "plt", label: "PLT" },
    ],
  },
  // CRP and ECG are a single entry each, so the row holds one box with no
  // sub-label repeating the heading. ECG takes a written comment.
  { key: "crp", label: "CRP", single: true },
  {
    key: "rft",
    label: "RFT",
    fields: [
      { name: "creatinine", label: "Creatinine" },
      { name: "urea", label: "Urea" },
      { name: "sodium", label: "Sodium" },
      { name: "potassium", label: "Potassium" },
      { name: "chloride", label: "Chloride" },
    ],
  },
  {
    key: "ecg",
    label: "ECG",
    single: true,
    comment: true,
    placeholder: "Comments",
  },
];

const EmergencyInvestigations = () => (
  <div className="emergency-investigations">
    {INVESTIGATION_GROUPS.map((group) => (
      <div className="emergency-inv-row" key={group.key}>
        <Text className="emergency-investigation-title">{group.label}</Text>
        <div className="emergency-inv-fields">
          {group.single ? (
            <Form.Item name={["investigations", group.key]} noStyle>
              <Input
                className={`emergency-inv-input${
                  group.comment ? " emergency-inv-input--comment" : ""
                }`}
                placeholder={group.placeholder}
              />
            </Form.Item>
          ) : (
            group.fields.map((field) => (
              <div className="emergency-inv-field" key={field.name}>
                <Text className="emergency-inv-label">{field.label}</Text>
                <Form.Item
                  name={["investigations", group.key, field.name]}
                  noStyle
                >
                  <Input className="emergency-inv-input" />
                </Form.Item>
              </div>
            ))
          )}
        </div>
      </div>
    ))}

    <div className="emergency-inv-row">
      <Text className="emergency-investigation-title">Others</Text>
      <div className="emergency-inv-fields">
        <Form.List name={["investigations", "others"]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <div className="emergency-inv-other" key={field.key}>
                  <Form.Item name={[field.name, "name"]} noStyle>
                    <Input
                      className="emergency-inv-input emergency-inv-input--name"
                      placeholder="Investigation"
                    />
                  </Form.Item>
                  <Form.Item name={[field.name, "value"]} noStyle>
                    <Input
                      className="emergency-inv-input"
                      placeholder="Value"
                    />
                  </Form.Item>
                  <Button
                    type="text"
                    aria-label="Remove investigation"
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(field.name)}
                  />
                </div>
              ))}
              <Button
                type="dashed"
                onClick={() => add({ name: "", value: "" })}
                icon={<PlusOutlined />}
              >
                New
              </Button>
            </>
          )}
        </Form.List>
      </div>
    </div>
  </div>
);

export default EmergencyInvestigations;
